import { Link } from "react-router-dom";

/**
 * Retention on the worklist is a GLANCE, not a queue. The full renewal pipeline —
 * every at-risk customer, the draft, the stage buttons — lives on the /retention tab;
 * duplicating it here buried the P0 fire queue under dozens of P2 cards. So the
 * worklist keeps one honest line ("retention second") and hands the work off.
 *
 * Styled as the tier HEADER that collapsed (dot · label · count · rule · figures),
 * NOT a boxed panel — it must not carry the same visual rank as the P0 fire queue,
 * and it must not re-box figures the glance already shows.
 *
 * DISPLAY-ONLY: the single action is a <Link> to /retention. Nothing here sends.
 */
export default function RetentionStrip({
  atRisk,
  expiringToday,
  inPipeline,
}: {
  atRisk: number;
  expiringToday: number;
  inPipeline: number;
}) {
  return (
    <section
      data-testid="worklist-retention-strip"
      aria-label="Retention summary"
      className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 py-1"
    >
      <span
        aria-hidden="true"
        className="h-2 w-2 rounded-sm bg-clinic-400 dark:bg-navy-600"
      />
      <h2 className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-clinic-600 dark:text-gray-400">
        P2 · Retention
      </h2>
      <span
        data-testid="retstrip-atrisk"
        className="rounded bg-clinic-100 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-clinic-600 ring-1 ring-clinic-200 dark:bg-navy-700 dark:text-gray-300 dark:ring-navy-600"
      >
        {atRisk} at-risk
      </span>

      {/* figures the glance does NOT already show; expiring rides warn (business risk,
          never crit — a P2 surface can't alarm, per TierSection's grammar) */}
      <span className="flex items-center gap-x-4 text-xs tabular-nums text-clinic-500 dark:text-gray-400">
        <span data-testid="retstrip-expiring">
          <span
            className={`font-semibold ${
              expiringToday > 0
                ? "text-warn-600 dark:text-amber-400"
                : "text-clinic-700 dark:text-gray-200"
            }`}
          >
            {expiringToday}
          </span>{" "}
          expire today
        </span>
        <span data-testid="retstrip-pipeline">
          <span className="font-semibold text-clinic-700 dark:text-gray-200">
            {inPipeline}
          </span>{" "}
          in pipeline
        </span>
      </span>

      <span className="hidden h-px flex-1 bg-clinic-100 dark:bg-navy-700 sm:block" />

      <Link
        to="/retention"
        data-testid="retstrip-open"
        className="shrink-0 rounded text-xs font-medium text-action-600 underline underline-offset-2 hover:text-action-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2 dark:text-action-300"
      >
        Open retention&nbsp;&rarr;
      </Link>
    </section>
  );
}
